import {
  AGENT_OR_STATUS_MESSAGE,
  BULK_OPERATIONS,
  SHIPPER_ESN_TYPES,
  SHIPPER_INCLUDE_ORDERS,
} from '@app/shared';
import type { BulkOperation, UnitStatus } from '@app/shared';

/** A field of a bulk screen beyond the identifiers; its value goes to the API as a parameter. */
export interface BulkExtra {
  /** Parameter name. */
  key: string;
  label: string;
  source: 'text' | 'options' | 'disconnectReason' | 'changeReason' | 'carrier';
  options?: readonly string[];
  required?: boolean;
  initial?: string;
  /** Shown with pasted values only: a file row carries its own value. */
  pasteOnly?: boolean;
  help?: string;
}

/** What differs between the bulk screens; one generic page renders them all. */
export interface BulkConfig {
  operation: BulkOperation;
  /** Screen title as the reference shows it. */
  title: string;
  /** Permission needed to submit. */
  code: string;
  /** Any of these may read the batch report. */
  reportCodes: string[];
  reportTitle: string;
  /** "Type - 1": identifiers pasted into a textarea. "Type - 2" (CSV file) is always there. */
  paste: boolean;
  /** Label of the pasted identifiers. */
  valueLabel: string;
  /** What the identifiers are, for helper texts: "SIMs" or "devices". */
  plural: string;
  /** Master / Distributor / Retailer / Employee selects for the target agent. */
  agent: 'required' | 'optional' | 'none';
  /** Status and change-reason selects; the statuses offered, in the order of the select. */
  status: readonly UnitStatus[] | null;
  /** Offer the CSV template for download. */
  template: boolean;
  fileHint: string;
  /** Column headers of the batch report, which differ between reference screens. */
  reportHeaders: {
    fileName?: string;
    uploadedAt: string;
    total: string;
    success: string;
    failure: string;
  };
  extras?: BulkExtra[];
  /** What the screen does and requires, shown above the form. */
  notes?: string[];
  /** Header row of the sample file offered for download. */
  sampleHeader?: string;
  /**
   * The status and the agent are each optional but one of them is needed for pasted values
   * (a file row carries its own); this is the message. No change reason is asked for.
   */
  agentOrStatus?: string;
  /** Asked before the batch is sent, in the reference's words. */
  confirm?: string;
}

const DEFAULT_HEADERS: BulkConfig['reportHeaders'] = {
  uploadedAt: 'Uploaded On',
  total: 'Total Records',
  success: 'Uploaded Success',
  failure: 'Uploaded Failure',
};

const SIM_FILE_HINT = 'CSV file with one column named "sim".';
const DEVICE_FILE_HINT = 'CSV file with one column named "imei".';

/** Order of the status select on the reference's "Change SIM Status & Agent Name" screen. */
export const BULK_STATUS_OPTIONS: readonly UnitStatus[] = [
  'Removed',
  'Bad',
  'Free',
  'Used',
  'Fraud',
  'Lost/Stolen',
];
/** Devices have no Fraud or Lost/Stolen status. Same order as the SIM screen (assumption). */
const DEVICE_STATUS_OPTIONS: readonly UnitStatus[] = ['Removed', 'Bad', 'Free', 'Used'];

/** Shippers offered on "Bulk Print Label". */
const LABEL_SHIPPERS = ['UPS', 'USPS', 'FEDEX', 'ENDICIA', 'STAMPS', 'SHIPST'] as const;

/** A line, customer or order tool: no agent or status selects; the file is described by its header. */
const tool = (
  config: Pick<BulkConfig, 'operation' | 'title' | 'code' | 'reportTitle' | 'sampleHeader'> &
    Partial<BulkConfig>,
): BulkConfig => ({
  reportCodes: [config.code],
  paste: false,
  valueLabel: 'MDN',
  plural: 'rows',
  agent: 'none',
  status: null,
  template: false,
  fileHint: `CSV file with the header ${config.sampleHeader}.`,
  reportHeaders: DEFAULT_HEADERS,
  ...config,
});

const UPDATE_REPORT_HEADERS: BulkConfig['reportHeaders'] = {
  fileName: 'Uploaded File Name',
  uploadedAt: 'Uploaded Date Time',
  total: 'Total Records',
  success: 'Total Success',
  failure: 'Total Failure',
};

export const BULK_CONFIGS: Record<BulkOperation, BulkConfig> = {
  'update-sims': {
    operation: 'update-sims',
    title: 'Update Inventory: ESN/SIM, Bulk Upload',
    code: 'PC391',
    reportCodes: ['PC391', 'PC188'],
    reportTitle: 'Inventory Update Batch Report (PC188)',
    paste: false,
    valueLabel: 'ESN/SIM',
    plural: 'SIMs',
    agent: 'none',
    status: null,
    template: false,
    fileHint: "Please Upload File To Update ESN/SIM's Details Into Inventory.",
    sampleHeader:
      'ESN/SIM,Model ID,MSL/PUK,PO#,Box,Notes,Wholesale Price,Selling Price,Activation Fee,BYOD (Y/N),MSID,UICCID,Device ID',
    reportHeaders: UPDATE_REPORT_HEADERS,
    extras: [{ key: 'carrierId', label: 'Carrier', source: 'carrier' }],
    notes: [
      'Each row updates the ESN/SIM it names; an empty cell keeps the stored value.',
      'Model ID is the number of the model (Manage Model). Prices are in dollars. Device ID tags that device to the SIM.',
      'The Carrier chosen here is set on every SIM of the file. Vendor is not offered: there is no vendor list here.',
    ],
  },
  'update-devices': {
    operation: 'update-devices',
    title: 'Update Inventory: DEVICE, Bulk Upload',
    code: 'PC391',
    reportCodes: ['PC391', 'PC476'],
    reportTitle: 'Inventory Update Device Batch Report (PC476)',
    paste: false,
    valueLabel: 'Device ID',
    plural: 'devices',
    agent: 'none',
    status: null,
    template: false,
    fileHint: 'Please Upload File To Update Device Details Into Inventory.',
    sampleHeader:
      'DeviceId,Model ID,Vendor,PO#,Box,Notes,Wholesale Price,Carrier ID,Shipper ID,Device Retail Price,BYOD (Y/N),ACP Co-Pay Amount,ACP Device Reimbursement Amount',
    reportHeaders: UPDATE_REPORT_HEADERS,
    notes: [
      'Each row updates the device it names; an empty cell keeps the stored value.',
      'Model ID is the number of the model (Manage Model); Carrier ID is the carrier code. Prices are in dollars.',
      'Vendor and Shipper ID are read but not stored: there are no vendor or shipper lists here.',
    ],
  },
  'adjust-sim': {
    operation: 'adjust-sim',
    title: 'Inventory SIM Re-assign',
    code: 'PC38',
    reportCodes: ['PC38', 'PC310'],
    reportTitle: 'Re-assign SIM Report (PC310)',
    paste: true,
    valueLabel: 'SIM',
    plural: 'SIMs',
    agent: 'required',
    status: null,
    template: false,
    fileHint: SIM_FILE_HINT,
    reportHeaders: DEFAULT_HEADERS,
  },
  'adjust-device': {
    operation: 'adjust-device',
    title: 'Inventory Device Re-assign',
    code: 'PC32',
    reportCodes: ['PC32', 'PC311'],
    reportTitle: 'Re-assign Device Report (PC311)',
    paste: true,
    valueLabel: 'Device ID',
    plural: 'devices',
    agent: 'required',
    status: null,
    template: false,
    fileHint: DEVICE_FILE_HINT,
    reportHeaders: DEFAULT_HEADERS,
  },
  'change-sim-status': {
    operation: 'change-sim-status',
    title: 'Change SIM Status & Agent Name',
    code: 'PC50',
    reportCodes: ['PC50'],
    reportTitle: 'Change SIM Status And Agent Name Report',
    paste: true,
    valueLabel: 'SIM',
    plural: 'SIMs',
    agent: 'optional',
    status: BULK_STATUS_OPTIONS,
    agentOrStatus: AGENT_OR_STATUS_MESSAGE,
    confirm: 'Are you sure, you want to proceed.?',
    template: false,
    fileHint:
      'CSV file with the header SIM, Agent ID (login ID), Status; an empty Agent ID or Status takes the choice above.',
    sampleHeader: 'SIM,Agent ID,Status',
    reportHeaders: {
      fileName: 'Uploaded File Name',
      uploadedAt: 'Uploaded Date Time',
      total: 'Total SIM',
      success: 'Total Success SIM',
      failure: 'Total Failure SIM',
    },
    notes: [
      'The SIM status will be changed to "USED" only for active subscribers.',
      'The SIM must exist in the system; you cannot reassign or change the status of a SIM that has never been uploaded.',
      'If the SIM status is "Used," you cannot change the status.',
    ],
  },
  'untag-device': {
    operation: 'untag-device',
    title: 'Untagged Device',
    code: 'PC345',
    reportCodes: ['PC345'],
    reportTitle: 'Untagged Device Details',
    paste: true,
    valueLabel: 'Device/IMEI',
    plural: 'devices',
    agent: 'none',
    status: null,
    confirm: 'Are you sure to untagged these Device',
    template: false,
    fileHint: DEVICE_FILE_HINT,
    sampleHeader: 'imei',
    reportHeaders: DEFAULT_HEADERS,
    notes: ['Removes the tag between each device and its SIM.'],
  },
  'reassign-sim': {
    operation: 'reassign-sim',
    title: 'Inventory SIM Re-assign',
    code: 'PC352',
    reportCodes: ['PC352', 'PC310'],
    reportTitle: 'Re-assign SIM Report (PC310)',
    paste: true,
    valueLabel: 'SIM',
    plural: 'SIMs',
    agent: 'required',
    status: null,
    template: false,
    fileHint: SIM_FILE_HINT,
    reportHeaders: DEFAULT_HEADERS,
  },
  'sim-status-reassign': {
    operation: 'sim-status-reassign',
    title: 'Change SIM Status & Agent Name',
    code: 'PC365',
    reportCodes: ['PC365'],
    reportTitle: 'Batch Report',
    paste: true,
    valueLabel: 'SIM',
    plural: 'SIMs',
    agent: 'optional',
    status: BULK_STATUS_OPTIONS,
    template: false,
    fileHint: SIM_FILE_HINT,
    // This reference screen names its report columns differently from the other bulk screens.
    reportHeaders: {
      fileName: 'Uploaded File Name',
      uploadedAt: 'Uploaded Date Time',
      total: 'Total SIM',
      success: 'Total Success SIM',
      failure: 'Total Failure SIM',
    },
  },
  'add-units': {
    operation: 'add-units',
    title: 'Add Bulk Unit',
    code: 'PC345',
    reportCodes: ['PC345'],
    reportTitle: 'Batch Report',
    paste: false,
    valueLabel: 'SIM / IMEI',
    plural: 'units',
    agent: 'none',
    status: null,
    template: true,
    fileHint:
      'CSV file with the columns of the template. Each row adds one SIM, one device, or both as a pair.',
    reportHeaders: DEFAULT_HEADERS,
  },
  'reassign-device': {
    operation: 'reassign-device',
    // The reference's page title was not recorded; this follows "Inventory SIM Re-assign".
    title: 'Inventory Device Re-assign',
    code: 'PC353',
    reportCodes: ['PC353', 'PC311'],
    reportTitle: 'Re-assign Device Report (PC311)',
    paste: true,
    valueLabel: 'Device Id',
    plural: 'devices',
    agent: 'required',
    status: null,
    template: false,
    fileHint: DEVICE_FILE_HINT,
    reportHeaders: DEFAULT_HEADERS,
  },
  'device-status-reassign': {
    operation: 'device-status-reassign',
    title: 'Change Device Status & Agent Name',
    code: 'PC364',
    reportCodes: ['PC364'],
    reportTitle: 'Batch Report',
    paste: true,
    valueLabel: 'Device Id',
    plural: 'devices',
    agent: 'optional',
    status: DEVICE_STATUS_OPTIONS,
    template: false,
    fileHint: DEVICE_FILE_HINT,
    reportHeaders: DEFAULT_HEADERS,
  },
  'clear-device': {
    operation: 'clear-device',
    // The reference's page title was not recorded; the menu label is used.
    title: 'Bulk Clear Device',
    code: 'PC354',
    reportCodes: ['PC354', 'PC180'],
    reportTitle: 'Clear Device Report (PC180)',
    paste: true,
    valueLabel: 'Device/IMEI',
    plural: 'devices',
    agent: 'none',
    status: null,
    template: false,
    fileHint: DEVICE_FILE_HINT,
    reportHeaders: DEFAULT_HEADERS,
  },
  'clear-sim': {
    operation: 'clear-sim',
    title: 'Clear SIM',
    code: 'PC351',
    reportCodes: ['PC351'],
    reportTitle: 'Batch Report',
    paste: true,
    valueLabel: 'SIM',
    plural: 'SIMs',
    agent: 'none',
    status: null,
    template: false,
    fileHint: SIM_FILE_HINT,
    reportHeaders: DEFAULT_HEADERS,
    notes: [
      'Clears SIMs that are marked Removed, Bad, Available or Lost/Stolen, and the SIMs of disconnected customers.',
      'A cleared SIM leaves its agent chain and becomes unassigned, Available stock.',
    ],
    sampleHeader: 'SIM',
  },
  'reassign-sim-by-agent': {
    operation: 'reassign-sim-by-agent',
    title: 'Bulk SIM Reassign by Agent',
    code: 'PC552',
    reportCodes: ['PC552'],
    reportTitle: 'Batch Report',
    paste: true,
    valueLabel: 'SIM',
    plural: 'SIMs',
    agent: 'required',
    status: null,
    template: false,
    fileHint: SIM_FILE_HINT,
    reportHeaders: DEFAULT_HEADERS,
  },
  'reassign-device-by-agent': {
    operation: 'reassign-device-by-agent',
    title: 'Bulk Device Reassign by Agent',
    code: 'PC551',
    reportCodes: ['PC551'],
    reportTitle: 'Batch Report',
    paste: true,
    valueLabel: 'Device Id',
    plural: 'devices',
    agent: 'required',
    status: null,
    template: false,
    fileHint: DEVICE_FILE_HINT,
    reportHeaders: DEFAULT_HEADERS,
  },
  'line-status': tool({
    operation: 'line-status',
    title: 'Bulk Line Status',
    code: 'PC361',
    reportTitle: 'View Report',
    sampleHeader: 'MDN,SIM',
    notes: [
      'Checks the line enquiry of each MDN at the carrier. MDN and SIM are both mandatory.',
      'Nothing is changed: the report shows the answer for each line.',
    ],
  }),
  'deactivate-mdn': tool({
    operation: 'deactivate-mdn',
    title: 'Deactivate MDN',
    code: 'PC356',
    reportCodes: ['PC356', 'PC23'],
    reportTitle: 'Deactivate MDN Report (PC23)',
    paste: true,
    plural: 'telephone numbers',
    sampleHeader:
      'MDN,Disconnect Reason ID(Numeric),BSS Disconnect(Y/N),Carrier Disconnect(Y/N),NLAD De-Enroll Type,Make EBB/ACP/LIFELINE Child As Parent',
    extras: [
      {
        key: 'reasonId',
        label: 'Disconnect Reason',
        source: 'disconnectReason',
        required: true,
        pasteOnly: true,
      },
      {
        key: 'bssDisconnect',
        label: 'BSS Disconnect',
        source: 'options',
        options: ['Y', 'N'],
        initial: 'Y',
        pasteOnly: true,
      },
      {
        key: 'carrierDisconnect',
        label: 'Carrier Disconnect',
        source: 'options',
        options: ['Y', 'N'],
        initial: 'Y',
        pasteOnly: true,
      },
    ],
    notes: [
      'The status of each MDN is changed to Inactive. The MDN must be in Active state.',
      'The line can be disconnected here (BSS Disconnect), at the carrier (Carrier Disconnect), or both.',
      'In a file, Disconnect Reason ID is the number of the reason in the disconnection reason list.',
      'You also need the Inactive permission of Update Account Status (PC637).',
    ],
  }),
  'swap-sim': tool({
    operation: 'swap-sim',
    title: 'Bulk Swap SIM',
    code: 'PC358',
    reportCodes: ['PC358', 'PC24'],
    reportTitle: 'Swap SIM Batch Report (PC24)',
    sampleHeader: 'OLD SIM,New SIM,Store ID(Employee)',
    extras: [
      {
        key: 'reasonId',
        label: 'Change Reason',
        source: 'changeReason',
        required: true,
        help: 'Decides what becomes of the old SIMs.',
      },
    ],
    notes: [
      'Only Active subscribers are allowed. OLD SIM and New SIM are mandatory.',
      'Store ID(Employee) is optional: the login ID of the agent the new SIM is assigned to.',
    ],
  }),
  'swap-mdn': tool({
    operation: 'swap-mdn',
    title: 'Bulk Swap MDN',
    code: 'PC357',
    reportCodes: ['PC357', 'PC540'],
    reportTitle: 'Bulk Swap MDN Report (PC540)',
    sampleHeader: 'OLD MDN,Zipcode',
    notes: [
      'Only Active, Dispatched and Suspended subscribers are allowed. OLD MDN and Zipcode are mandatory.',
      'If service is available in the ZIP code, the carrier issues a new MDN for the line.',
    ],
  }),
  'swap-mdn-by-customer': tool({
    operation: 'swap-mdn-by-customer',
    title: 'Bulk Swap MDN By customer ID',
    code: 'PC804',
    reportCodes: ['PC804', 'PC540'],
    reportTitle: 'Bulk Swap MDN Report (PC540)',
    valueLabel: 'Cust ID',
    sampleHeader: 'Cust ID,ESN,Zipcode',
    notes: ['Cust ID and Zipcode are mandatory. ESN, when given, must belong to the customer.'],
  }),
  'mdn-topup': tool({
    operation: 'mdn-topup',
    title: 'Bulk MDN Topup',
    code: 'PC830',
    reportCodes: ['PC830', 'PC831'],
    reportTitle: 'MDN Topup Report (PC831)',
    sampleHeader: 'MDN,SIM,Plan ID',
    notes: [
      'Plan ID is the top-up to apply to the customer. Enter the name of the top-up as it is in Topups.',
      'The line must be Active. No payment is collected.',
    ],
  }),
  'customer-plan-change': tool({
    operation: 'customer-plan-change',
    title: 'Bulk Customer Plan Change',
    code: 'PC360',
    reportCodes: ['PC360', 'PC496'],
    reportTitle: 'Bulk Customer Plan Change Report (PC496)',
    sampleHeader:
      'MDN,Customer ID,New Carrier Code,New Plan Code,Schedule Date (YYYY-MM-DD),LTE Plan (N/Y)',
    notes: [
      'New Carrier Code is the carrier code the new plan is built on. New Plan Code is needed only when several plans use that code.',
      'Give the MDN, the Customer ID, or both. The plan is changed at once: a future Schedule Date is refused.',
    ],
  }),
  hotline: tool({
    operation: 'hotline',
    title: 'Bulk Hotline',
    code: 'PC362',
    reportCodes: ['PC362', 'PC436'],
    reportTitle: 'Bulk Hotline Report (PC436)',
    paste: true,
    plural: 'telephone numbers',
    sampleHeader: 'MDN',
    extras: [{ key: 'carrierId', label: 'Carrier', source: 'carrier' }],
    notes: ['Changes the state of each line from Active to Restricted at the carrier.'],
  }),
  unhotline: tool({
    operation: 'unhotline',
    title: 'Bulk Unhotline',
    code: 'PC363',
    reportCodes: ['PC363', 'PC437'],
    reportTitle: 'Bulk Unhotline Report (PC437)',
    paste: true,
    plural: 'telephone numbers',
    sampleHeader: 'MDN',
    extras: [{ key: 'carrierId', label: 'Carrier', source: 'carrier' }],
    notes: ['Changes the state of each line from Restricted to Active at the carrier.'],
  }),
  'deprovision-sim': tool({
    operation: 'deprovision-sim',
    title: 'De-Provision Unused SIM',
    code: 'PC355',
    reportCodes: ['PC355', 'PC22'],
    reportTitle: 'De-enroll SIM Report (PC22)',
    paste: true,
    valueLabel: 'SIM',
    plural: 'SIMs',
    sampleHeader: 'SIM',
    extras: [{ key: 'remark', label: 'Remark', source: 'text', required: true }],
    notes: [
      'De-provisions a provisioned SIM that no customer uses. A justification (Remark) is required.',
    ],
  }),
  'print-label': tool({
    operation: 'print-label',
    title: 'Bulk Print Label',
    code: 'PC884',
    reportTitle: 'Print Label Report (PC884)',
    paste: true,
    valueLabel: 'SIM',
    plural: 'SIMs',
    sampleHeader: 'SIM',
    extras: [
      {
        key: 'shipper',
        label: 'Shipper',
        source: 'options',
        options: LABEL_SHIPPERS,
        required: true,
      },
    ],
    notes: [
      'Makes a shipping label for each order that has one of these SIMs assigned and has not been shipped.',
    ],
    reportHeaders: { ...DEFAULT_HEADERS, success: 'Labels' },
  }),
  'clear-customer-device': tool({
    operation: 'clear-customer-device',
    title: 'Clear Customer Device',
    code: 'PC868',
    reportCodes: ['PC868', 'PC869'],
    reportTitle: 'Clear Customer Device Report (PC869)',
    paste: true,
    valueLabel: 'Device/IMEI',
    plural: 'devices',
    sampleHeader: 'Device/IMEI',
    notes: [
      'Clears the device of customers whose status is Active, Suspended or Dispatched.',
      'The agent chain is not changed; the device only becomes Available.',
    ],
  }),
  'customer-update': tool({
    operation: 'customer-update',
    title: 'CSV Bulk Customer Update',
    code: 'PC856',
    reportTitle: 'Bulk process customer update Batch Report',
    valueLabel: 'Customer Id',
    sampleHeader:
      'Customer Id,First Name,Middle Name,Last Name,Address,State,Zip Code,Email,Password,Alternate number',
    notes: [
      'An empty cell keeps the current value. Address, State and Zip Code change the service address.',
      'The Password column is ignored: customers have no login here.',
    ],
  }),
  'import-customers': tool({
    operation: 'import-customers',
    title: 'Import customers from the previous system',
    code: 'PC856',
    reportTitle: 'Import customers Batch Report',
    valueLabel: 'Main MDN',
    sampleHeader:
      'Customer ID,Enrollment ID,First Name,Middle Name,Last Name,Address 1,Address 2,City,State,Zip,Email Address,Contact Phone,Main MDN,SIM,IMEI,Carrier,Current Plan Name,Subscriber Status,Activation Date,Disconnection Date,Plan Expiry Date,Wallet Balance,Agent Login ID',
    notes: [
      'Use the "Subscriber Information" export of the previous system (Telgoo5 Customize Reports) with the plan expiry date and wallet balance added if you have them. Administrators only.',
      'Nothing is sent to the carrier: the lines are already live there. Each row makes the customer, puts its SIM in inventory (Used) and records a completed enrollment.',
      'Carrier is the code of Plan Config > Carrier Setup; Current Plan Name must match a plan of that carrier. Subscriber Status: Active, Suspend (or Hotline), Inactive, Dispatched.',
      'The file’s Customer ID and Enrollment ID are kept when free, so staff and customers keep their numbers. Dates as MM-DD-YYYY or YYYY-MM-DD.',
      'Import a few rows first and check them on the customer page, then the rest.',
    ],
  }),
  'nlad-process': tool({
    operation: 'nlad-process',
    title: 'CSV Bulk NLAD Update',
    code: 'PC857',
    reportTitle: 'Bulk process NLAD update Batch Report',
    valueLabel: 'Customer ID',
    sampleHeader:
      'Customer ID,Nlad Transaction Type,Reconcilation Batch Number,Notes,Phone Number In NLAD,Transaction Effective Date,Subscriber ID,Service Initialization Date,Rep ID,Rep Not Assist,Service Type,Consumer Email,Eligibility Code,Expected Rate,ebbpTribalBenefitFlag,expectedRateDevice,dupAddressException',
    notes: [
      'Customer ID and Nlad Transaction Type are required.',
      'Each update goes to NLAD through Settings > Connections > Lifeline (NLAD) and is recorded in the API log.',
    ],
  }),
  'sim-device-information': tool({
    operation: 'sim-device-information',
    title: 'Bulk SIM Device Information',
    code: 'PC1058',
    reportTitle: 'Batch Report',
    paste: true,
    valueLabel: 'SIM / Device ID',
    plural: 'SIMs or devices',
    sampleHeader: 'SIM/Device ID',
    notes: [
      'Looks up each SIM or device in inventory. Nothing is changed: the report shows status, carrier, model, agent, MDN and customer.',
    ],
  }),
  'bulk-notes': tool({
    operation: 'bulk-notes',
    title: 'Note Type (Bulk Upload Notes)',
    code: 'PC449',
    reportTitle: 'Batch Report',
    valueLabel: 'Customer ID',
    sampleHeader: 'Customer ID,Note Type,Notes',
    notes: [
      'Load CSV to bulk upload notes to multiple accounts.',
      'Note Type is the name of an Active note type. An empty Notes cell takes the note text of that type.',
      "The file layout of the reference was not seen; these three columns are this application's.",
    ],
  }),
  'order-fulfillment': tool({
    operation: 'order-fulfillment',
    title: 'Order Fulfillment (Bulk Fulfillment)',
    code: 'PC1066',
    reportTitle: 'Batch Report',
    valueLabel: 'Enrollment ID',
    sampleHeader: 'Enrollment ID,SIM,Device ID',
    notes: [
      'Each row gives one New order of the Shipping Queue a SIM from your stock, and a device when Device ID is filled in.',
      'The shipper is not part of this file: it is chosen when the parcel is scanned for shipment.',
      "The reference shows only a .csv file input for this dialog; the three columns are this application's.",
    ],
  }),
  'shipper-inventory': tool({
    operation: 'shipper-inventory',
    title: 'Manage Shipper Inventory: Bulk Upload',
    code: 'PC7',
    reportTitle: 'Shipper Batch Report',
    valueLabel: 'ESN/SIM',
    sampleHeader:
      'ESN/SIM,ESN/SIM Model,Shipper ID,Carrier,UICCID,Enrollment ID,Device,Phone Model,State,Phone Type,Areacode',
    extras: [
      {
        key: 'esnType',
        label: 'ESN/SIM Type',
        source: 'options',
        options: SHIPPER_ESN_TYPES,
        required: true,
      },
      {
        key: 'includeOrders',
        label: 'Include Orders',
        source: 'options',
        options: SHIPPER_INCLUDE_ORDERS,
        required: true,
      },
    ],
    notes: [
      'Each row adds one SIM (and its device) to your stock for the shipper; a row with an Enrollment ID of an order waiting in the Shipping Queue gives the SIM to that order.',
      'ESN/SIM Model and Phone Model are model IDs; Carrier is the carrier code; Shipper ID is the shipper (UPS, USPS, FEDEX, ENDICIA, STAMPS, NONE, SHIPST).',
    ],
  }),
  'bulk-enrollment': tool({
    operation: 'bulk-enrollment',
    title: 'Bulk enrollment',
    code: 'PC731',
    reportCodes: ['PC731', 'PC794'],
    reportTitle: 'Bulk Enrollment Report (PC794)',
    valueLabel: 'SIM/IMEI',
    sampleHeader:
      'ZIP CODE,PLAN ID,SIM/IMEI,TELEPHONE,FIRST NAME,MIDDLE NAME,LAST NAME,Address 1,Address 2,ADVANCE BILLING MONTH,Payment Method,DEVICE ID,DEVICE HANDOVER TYPE,EMAIL',
    notes: [
      'Header is required. Mandatory: ZIP CODE, PLAN ID (the plan code), SIM/IMEI, FIRST NAME, LAST NAME and Address 1.',
      'Payment Method is Cash or Skip Payment; an empty cell means Skip Payment. Credit Card, Dealer Wallet, Paypal and PIN are not allowed.',
      'Each row is enrolled with the SIM in hand. DEVICE ID and DEVICE HANDOVER TYPE must be empty.',
    ],
  }),
};

export const bulkConfigFor = (operation: string | undefined): BulkConfig | undefined =>
  (BULK_OPERATIONS as readonly string[]).includes(operation ?? '')
    ? BULK_CONFIGS[operation as BulkOperation]
    : undefined;

export const MAX_BULK_VALUES = 5000;

/** Splits pasted text into values: one per line, or separated by commas, semicolons or spaces. */
export const parseIdentifiers = (text: string): string[] =>
  text
    .split(/[\s,;]+/)
    .map((value) => value.trim())
    .filter(Boolean);
